import React, { useEffect, useState, useMemo } from "react";
import "./LandingPage.css"
import { Link } from "react-router-dom"
import Plans from "./components/plans"
import ourAppLogo from './logos/ourapp.PNG';
import WelcomePopup from "./components/WelcomePopup";
import SeoHead from "./components/SeoHead";
import ArticlesNav from "./components/ArticlesNav";
import PreferredSourceButton from "./components/PreferredSourceButton";
import { HOME_SEO, SITE_URL } from "./utils/seo";
import { getProductTerms } from "./landingCopy";
import { prefetchShopCatalog } from "./utils/shopSpeed";

const CATALOG_CALLOUTS = {
  fr: {
    left: [
      { title: "Promotions", subtitle: "−% sur tout, une catégorie ou un produit. Prix barré, filtre express." },
      { title: "Variantes", subtitle: "Plusieurs choix, prix et stock nets. Panier sur mesure." },
      { title: "Options +", subtitle: "Suppléments et extras, réglés unité par unité." },
    ],
    right: [
      { title: "Bandeaux", subtitle: "Grande bannière, carrousel promo, encart d’annonce coloré." },
      { title: "Certificats", subtitle: "Vos PDF dans la fiche coop : aperçu pro, zoom au doigt." },
      { title: "WhatsApp", subtitle: "Panier + total + coordonnées : envoyé en un clic." },
    ],
  },
  ar: {
    left: [
      { title: "عروض", subtitle: "خصم بالنسبة حيث تريدون. سعر مشطوب وفرز سريع." },
      { title: "تنويعات", subtitle: "خيارات متعددة، أسعار واضحة. سلة على المقاس." },
      { title: "إضافات", subtitle: "خيارات إضافية لكل وحدة — مرنة وواضحة." },
    ],
    right: [
      { title: "لافتات", subtitle: "بانر قوي، شريط صور، إعلان ملوّن في الرأس." },
      { title: "شهادات", subtitle: "PDF في ملفكم : معاينة أنيقة وتكبير سهل." },
      { title: "واتساب", subtitle: "السلة والمجموع والبيانات — إرسال بنقرة." },
    ],
  },
};

function CatalogCalloutArrow({ toward }) {
  const isRight = toward === "right";
  return (
    <span className="catalog-callout-arrow" aria-hidden="true">
      <svg className="catalog-callout-arrow__svg" viewBox="0 0 120 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        {isRight ? (
          <>
            <path
              className="catalog-callout-arrow__line"
              d="M0 16h88"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <path
              className="catalog-callout-arrow__head"
              d="M88 8l20 8-20 8"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        ) : (
          <>
            <path
              className="catalog-callout-arrow__line"
              d="M120 16H32"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <path
              className="catalog-callout-arrow__head"
              d="M32 8L12 16l20 8"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        )}
      </svg>
    </span>
  );
}

export default function LandingPage({ language: appLanguage, setLanguage: setAppLanguage }) {
  const [language, setLanguage] = useState(appLanguage || 'fr');
  const [showFab, setShowFab] = useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [catalogVideoIndex, setCatalogVideoIndex] = useState(0);
  const [catalogVideoFallback, setCatalogVideoFallback] = useState(false);

  const publicUrl = process.env.PUBLIC_URL || "";
  const defaultCatalogVideoUrl =
    "https://res.cloudinary.com/diahtaejv/video/upload/v1775697486/Product_catalogue_gyybyn.mp4";
  /** Optional override: REACT_APP_CATALOG_VIDEO_URL. Then Cloudinary default, then files in public/. */
  const catalogVideoCandidates = [
    process.env.REACT_APP_CATALOG_VIDEO_URL?.trim(),
    defaultCatalogVideoUrl,
    `${publicUrl}/catalog-preview.mp4`,
    `${publicUrl}/product%20catalogue.mp4`,
  ].filter(Boolean);
  const catalogVideoSrc = catalogVideoCandidates[catalogVideoIndex] || "";
  const catalogStaticFallbackSrc = `${publicUrl}/landing/catalog-demo.svg`;

  useEffect(() => {
    setLanguage(appLanguage || 'fr');
  }, [appLanguage]);

  useEffect(() => {
    const onScroll = () => {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (!isMobile) {
        setShowFab(false);
        return;
      }
      setShowFab(window.scrollY > 80);
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Show welcome popup for all visitors until they close it
  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('hasSeenWelcomePopup')
    
    // Show popup for all visitors who haven't seen it
    if (!hasSeenWelcome) {
      setTimeout(() => {
        setShowWelcomePopup(true)
      }, 2000) // Show after 2 seconds
    }
  }, []);

  const toggleLanguage = () => {
    const next = language === 'fr' ? 'ar' : 'fr';
    setLanguage(next);
    if (typeof setAppLanguage === 'function') setAppLanguage(next);
    try { localStorage.setItem('language', next); } catch {}
  };

  const catalogCopy = CATALOG_CALLOUTS[language === "ar" ? "ar" : "fr"];
  const seoCopy = HOME_SEO[language === "ar" ? "ar" : "fr"];
  const terms = getProductTerms(language);
  const homeJsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: 'Taaouniaty',
        url: SITE_URL,
        logo: `${SITE_URL}/preview.png`,
        description: seoCopy.description,
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: 'Taaouniaty',
        publisher: { '@id': `${SITE_URL}/#organization` },
        inLanguage: ['fr', 'ar'],
      },
    ],
  }), [seoCopy.description, language]);

  return (
    <div className="landing-container">
      <SeoHead
        title={seoCopy.title}
        description={seoCopy.description}
        url={SITE_URL}
        language={language === 'ar' ? 'ar' : 'fr'}
        jsonLd={homeJsonLd}
      />
      {/* Header */}
      <header className="header">
        <nav className="nav">
          <div className="nav-brand">
            <h2>Taaouniaty</h2>
            <span className="brand-tagline">Pro</span>
          </div>
          <div className="nav-links" style={{ marginLeft: '150px' }}>
            <a href="#features">{language === 'fr' ? 'Fonctionnalités' : 'الميزات'}</a>
            <Link
              to="/marketplace"
              target="_blank"
              rel="noopener noreferrer"
              onMouseEnter={() => prefetchShopCatalog().catch(() => {})}
              onFocus={() => prefetchShopCatalog().catch(() => {})}
            >
              {language === 'fr' ? 'Marketplace' : 'المتجر'}
            </Link>
            <a href="#pricing">{language === 'fr' ? 'Tarifs' : 'الأسعار'}</a>
            <ArticlesNav language={language} newTab />
            <a href="#contact">{language === 'fr' ? 'Contact' : 'اتصل بنا'}</a>
          </div>
          <div className="nav-cta">
            <button
              onClick={toggleLanguage}
              className="desktop-only"
              style={{
                background: 'none',
                border: 'none',
                color: '#ea580c',
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: 'pointer',
                marginRight: '12px',
                padding: '8px 12px',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                textDecoration: 'none'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#fef2f2';
                e.target.style.color = '#ea580c';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#ea580c';
              }}
            >
              {language === 'fr' ? 'العربية' : 'Français'}
            </button>
            <Link to="/login" className="desktop-only" style={{ textDecoration: 'none' }}>
              <button className="btn-outline" style={{ marginRight: '12px', fontSize: '0.9rem', fontWeight: 600, padding: '0.45rem 1rem' }}>
                {language === 'fr' ? 'Se connecter' : 'تسجيل الدخول'}
              </button>
            </Link>
            <Link to="/register" style={{ textDecoration: 'none' }}>
              <button className="btn-nav">{language === 'fr' ? 'Essai Gratuit' : 'تجربة مجانية'}</button>
            </Link>         
          </div>
        </nav>
      </header>
      {/* Mobile floating language toggle */}
      <button
        className={`mobile-lang-fab ${showFab ? 'visible' : ''}`}
        onClick={toggleLanguage}
        aria-label="Toggle language"
        title={language === 'fr' ? 'Passer en Arabe' : 'التحويل إلى الفرنسية'}
      >
        {language === 'fr' ? 'العربية' : 'Français'}
      </button>

      {/* HERO SECTION */}
      <section className="hero" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '90vh', background: 'linear-gradient(135deg, #fff5f0 0%, #fff8f5 100%)' }}>
        <div style={{ width: '100%', maxWidth: 700, textAlign: 'center', margin: '0 auto' }}>
          <div className="hero-text" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div className="hero-badge">
              <span>{language === 'fr' ? 'La plateforme intelligente pour les coopératives marocaines' : 'المنصة الذكية للتعاونيات المغربية'}</span>
            </div>
            <h1 className="hero-title">
              {language === 'fr' ? 'Votre coopérative mérite un' : 'تعاونيتك تستحق'}
              <span className="gradient-text"> {language === 'fr' ? 'assistant digital moderne' : 'مساعد رقمي حديث'}</span>
            </h1>
            <p className="hero-subtitle">
              {language === 'fr' ? 'Taaouniaty est la solution de gestion moderne et facile à utiliser, conçue spécialement pour les coopératives marocaines. Centralisez la gestion de vos membres, produits, ventes, achats et finances tout en un seul endroit, sans compétences techniques requises.' : 'تعاونيتي هي حل الإدارة الحديثة وسهلة الاستخدام، مصممة خصيصًا للتعاونيات المغربية. ركز إدارة أعضائك ومنتجاتك ومبيعاتك ومشترياتك وتمويلك في مكان واحد، دون الحاجة إلى مهارات تقنية.'}
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-number">50+</span>
                <span className="stat-label">{language === 'fr' ? 'Coopératives' : 'تعاونيات'}</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">10,000+</span>
                <span className="stat-label">{language === 'fr' ? 'Transactions' : 'معاملات'}<br />{language === 'fr' ? 'enregistrées' : 'مسجلة'}</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">99.9%</span>
                <span className="stat-label">{language === 'fr' ? 'disponibilité' : 'التوفر'}</span>
              </div>
            </div>
            <div className="hero-buttons">
              <button className="btn-primary" onClick={() => window.location.href = '/register'}>
                <span>{language === 'fr' ? 'Commencer Gratuitement' : 'ابدأ مجاناً'}</span>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12h14M12 5l7 7-7 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
            <div className="hero-trust">
              <span>{language === 'fr' ? 'Utilisé par les meilleures coopératives du Maroc' : 'يستخدمها أفضل التعاونيات في المغرب'}</span>
            </div>
          </div>
                </div>
      </section>

      <div className="hero-after-line" aria-hidden="true" />

      <section id="growth" className={`growth-map${language === "ar" ? " growth-map--rtl" : ""}`}>
        <div className="growth-map__texture" aria-hidden="true" />
        <div className="container growth-map__inner">
          <div className="growth-map__intro">
            <div className="section-badge">{language === "fr" ? "Votre parcours" : "مساركم"}</div>
            <h2 className="growth-map__title">
              {language === "fr" ? (
                <>Une infrastructure numérique qui <span className="gradient-text">évolue avec vous</span></>
              ) : (
                <>بنية رقمية <span className="gradient-text">تنمو معكم</span></>
              )}
            </h2>
            <p className="growth-map__lede">
              {language === "fr"
                ? `Du site web au ${terms.erp}, jusqu'au ${terms.pos} : activez ce dont vous avez besoin à chaque étape de croissance, sans changer d'outil.`
                : `من الموقع الإلكتروني إلى ${terms.erp} ثم ${terms.posSystem} : فعّلوا ما تحتاجونه في كل مرحلة دون تغيير الأدوات.`}
            </p>
            <p className="growth-map__tagline">
              {language === "fr"
                ? "Un itinéraire simple — comme sur une carte — pour lancer, structurer et accélérer."
                : "مسار واضح — كالخريطة — للانطلاق، التنظيم، والتسارع."}
            </p>
          </div>

          <div className="growth-map__route" role="list" aria-label={language === "fr" ? "Étapes du parcours" : "مراحل المسار"}>
            <div className="growth-map__route-line" aria-hidden="true" />

            <div className="growth-map__stop" role="listitem">
              <div className="growth-map__pin-col">
                <div className="growth-map__pin">
                  <span className="growth-map__pin-num">1</span>
                </div>
                <span className="growth-map__pin-caption">
                  {language === "fr" ? "Départ" : "انطلاق"}
                </span>
              </div>
              <div className="growth-map__stop-card">
                <span className="growth-map__plan-pill">E-Commerce Starter</span>
                <h3 className="growth-map__stop-title">{language === "fr" ? "Site web" : "الموقع الإلكتروني"}</h3>
                <p className="growth-map__stop-text">
                  {language === "fr"
                    ? `Votre site web : commandes, panier et WhatsApp — vendez tout de suite, sans attendre un ${terms.erp} complet.`
                    : `موقعكم الإلكتروني: طلبات، سلة وواتساب — بيعوا فوراً دون انتظار ${terms.erp} كامل.`}
                </p>
              </div>
            </div>

            <div className="growth-map__connector" aria-hidden="true">
              <svg width="48" height="24" viewBox="0 0 48 24" fill="none" className="growth-map__connector-svg">
                <path
                  d="M4 12h32M34 8l8 4-8 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <div className="growth-map__stop" role="listitem">
              <div className="growth-map__pin-col">
                <div className="growth-map__pin">
                  <span className="growth-map__pin-num">2</span>
                </div>
                <span className="growth-map__pin-caption">
                  {language === "fr" ? "Structurer" : "هيكلة"}
                </span>
              </div>
              <div className="growth-map__stop-card">
                <span className="growth-map__plan-pill">{terms.erpCap}</span>
                <h3 className="growth-map__stop-title">{terms.erpCap}</h3>
                <p className="growth-map__stop-text">
                  {language === "fr"
                    ? "Stocks, achats, membres, finances et rapports : le pilotage de la coopérative au même endroit."
                    : "مخزون، مشتريات، أعضاء، مالية وتقارير: كل التحكم في مكان واحد."}
                </p>
              </div>
            </div>

            <div className="growth-map__connector" aria-hidden="true">
              <svg width="48" height="24" viewBox="0 0 48 24" fill="none" className="growth-map__connector-svg">
                <path
                  d="M4 12h32M34 8l8 4-8 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <div className="growth-map__stop" role="listitem">
              <div className="growth-map__pin-col">
                <div className="growth-map__pin growth-map__pin--goal">
                  <span className="growth-map__pin-num">3</span>
                </div>
                <span className="growth-map__pin-caption">
                  {language === "fr" ? "Accélérer" : "تسارع"}
                </span>
              </div>
              <div className="growth-map__stop-card growth-map__stop-card--goal">
                <span className="growth-map__plan-pill">Full Ecosystem</span>
                <h3 className="growth-map__stop-title">{terms.posSystemCap}</h3>
                <p className="growth-map__stop-text">
                  {language === "fr"
                    ? `Caisse en magasin, stock aligné avec votre site web et le ${terms.erp} : prêt quand vous ouvrez le ${terms.pos}.`
                    : `صندوق في المحل ومخزون متوافق مع موقعكم و${terms.erp} عند فتح ${terms.pos}.`}
                </p>
              </div>
            </div>
          </div>

          <p className="growth-map__footer-note">
            {language === "fr"
              ? "Vous avancez étape par étape — la plateforme grandit avec vous."
              : "تقدمون خطوة بخطوة — المنصة تنمو معكم."}
          </p>
        </div>
      </section>

      <section className="website-feature-grid" aria-labelledby="website-features-title">
        <div className="container">
          <h2 id="website-features-title" className="website-feature-grid__title">
            {language === "fr" ? "Votre catalogue sur mobile" : "كتالوجكم على الجوال"}
          </h2>
          <div className={`catalog-showcase${language === "ar" ? " catalog-showcase--ar" : ""}`}>
            <ul
              className="catalog-callouts catalog-callouts--left"
              aria-label={language === "fr" ? "Fonctionnalités catalogue" : "ميزات الكتالوج"}
            >
              {catalogCopy.left.map((item) => (
                <li key={item.title} className="catalog-callout catalog-callout--from-left">
                  <div className="catalog-callout__text">
                    <strong className="catalog-callout__title">{item.title}</strong>
                    <span className="catalog-callout__subtitle">{item.subtitle}</span>
                  </div>
                  <CatalogCalloutArrow toward="right" />
                </li>
              ))}
            </ul>

            <div className="catalog-phone-wrap catalog-phone-wrap--center">
              <div className="catalog-phone-mockup">
                <div className="catalog-phone-notch" aria-hidden="true" />
                <div className="catalog-phone-screen">
                  {catalogVideoFallback || !catalogVideoSrc ? (
                    <img
                      className="catalog-phone-video"
                      src={catalogStaticFallbackSrc}
                      alt=""
                    />
                  ) : (
                    <video
                      key={catalogVideoIndex}
                      className="catalog-phone-video"
                      src={catalogVideoSrc}
                      autoPlay
                      muted
                      loop
                      playsInline
                      controls={false}
                      preload="metadata"
                      onLoadedMetadata={(event) => {
                        event.currentTarget.playbackRate = 0.75;
                      }}
                      onError={() => {
                        if (catalogVideoIndex < catalogVideoCandidates.length - 1) {
                          setCatalogVideoIndex((i) => i + 1);
                        } else {
                          setCatalogVideoFallback(true);
                        }
                      }}
                    />
                  )}
                </div>
              </div>
            </div>

            <ul
              className={`catalog-callouts catalog-callouts--right${catalogCopy.right.length < 3 ? " catalog-callouts--few" : ""}`}
              aria-label={language === "fr" ? "Autres fonctionnalités" : "مزايا أخرى"}
            >
              {catalogCopy.right.map((item) => (
                <li key={item.title} className="catalog-callout catalog-callout--from-right">
                  <CatalogCalloutArrow toward="left" />
                  <div className="catalog-callout__text">
                    <strong className="catalog-callout__title">{item.title}</strong>
                    <span className="catalog-callout__subtitle">{item.subtitle}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Dashboard Preview Section with Floating Cards */}
      <section className="dashboard-preview-section">
        <style>
          {`
            @keyframes float {
              0%, 100% {
                transform: translateY(0px) rotate(5deg);
              }
              50% {
                transform: translateY(-15px) rotate(5deg);
              }
            }
            .floating-card:nth-child(2) {
              animation-delay: 1.5s;
            }
            .floating-card:nth-child(3) {
              animation-delay: 3s;
            }
            .floating-card:nth-child(4) {
              animation-delay: 4.5s;
            }
          `}
        </style>
        <div className="container">
          <p className="dashboard-preview-eyebrow">
            {language === 'fr'
              ? `${terms.erpCap} · tableau de bord & opérations`
              : `${terms.erpCap} · لوحة التحكم والعمليات`}
          </p>
          <img
            src={ourAppLogo}
            alt="Dashboard Taaounity"
            style={{
              maxWidth: '850px',
              width: '100%',
              height: 'auto',
              borderRadius: '16px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
              zIndex: 1,
              position: 'relative',
              filter: 'blur(1px)',
              opacity: 0.85
            }}
          />

          <div className="floating-card floating-card--1" style={{
            position: 'absolute',
            right: '15%',
            top: '12%',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.25), 0 8px 25px rgba(0,0,0,0.15), 0 4px 15px rgba(0,0,0,0.1)',
            border: '1px solid #f3f4f6',
            padding: '16px',
            maxWidth: '240px',
            zIndex: 2,
            transform: 'rotate(5deg)',
            animation: 'float 6s ease-in-out infinite'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                backgroundColor: '#fef2f2',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid #fecaca'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: '0 0 4px 0' }}>{language === 'fr' ? 'Dettes Fournisseurs' : 'ديون الموردين'}</h4>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>15,500 MAD</p>
              </div>
            </div>
          </div>

          <div className="floating-card floating-card--2" style={{
            position: 'absolute',
            left: '12%',
            bottom: '15%',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.25), 0 8px 25px rgba(0,0,0,0.15), 0 4px 15px rgba(0,0,0,0.1)',
            border: '1px solid #f3f4f6',
            padding: '16px',
            maxWidth: '220px',
            zIndex: 2,
            transform: 'rotate(-3deg)',
            animation: 'float 6s ease-in-out infinite 1.5s'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                backgroundColor: '#f0fdf4',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid #bbf7d0'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: '0 0 4px 0' }}>{language === 'fr' ? 'Revenus du Mois' : 'إيرادات الشهر'}</h4>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>45,200 MAD</p>
              </div>
            </div>
          </div>

          <div className="floating-card floating-card--3" style={{
            position: 'absolute',
            left: '15%',
            top: '18%',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.25), 0 8px 25px rgba(0,0,0,0.15), 0 4px 15px rgba(0,0,0,0.1)',
            border: '1px solid #f3f4f6',
            padding: '16px',
            maxWidth: '200px',
            zIndex: 2,
            transform: 'rotate(-2deg)',
            animation: 'float 6s ease-in-out infinite 3s'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                backgroundColor: '#fffbeb',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid #fde68a'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: '0 0 4px 0' }}>{language === 'fr' ? 'Dettes Clients' : 'ديون العملاء'}</h4>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>8,300 MAD</p>
              </div>
            </div>
          </div>

          <div className="floating-card floating-card--4" style={{
            position: 'absolute',
            right: '12%',
            bottom: '12%',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.25), 0 8px 25px rgba(0,0,0,0.15), 0 4px 15px rgba(0,0,0,0.1)',
            border: '1px solid #f3f4f6',
            padding: '16px',
            maxWidth: '210px',
            zIndex: 2,
            transform: 'rotate(3deg)',
            animation: 'float 6s ease-in-out infinite 4.5s'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                backgroundColor: '#eff6ff',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid #bfdbfe'
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 3v18h18"/>
                  <path d="M18 17V9"/>
                  <path d="M13 17V5"/>
                  <path d="M8 17v-3"/>
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#111827', margin: '0 0 4px 0' }}>{language === 'fr' ? 'Bénéfice Net' : 'صافي الربح'}</h4>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>21,400 MAD</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <div className="section-header features-section__header">
            <div className="section-badge features-section__badge">{language === 'fr' ? 'Que fait Taaouniaty ?' : 'ماذا تفعل تعاونيتي؟'}</div>
            <h2 className="section-title features-section__title">
              <span className="features-section__title-line">
                {language === 'fr'
                  ? `Site web, ${terms.erp} et ${terms.pos}`
                  : `موقع إلكتروني و${terms.erp} و${terms.posSystem}`}
              </span>
              <span className="features-section__title-line features-section__title-line--accent">
                <span className="gradient-text">
                  {language === 'fr' ? 'réunis sur une plateforme' : 'في منصة واحدة'}
                </span>
              </span>
            </h2>
            <p className="section-subtitle features-section__subtitle">
              {language === 'fr' ? 'Site web pour vendre en ligne, gestion interne complète et caisse connectée : les briques que les coopératives utilisent déjà sur Taaouniaty, avec des droits et des forfaits adaptés à chaque étape.' : 'موقع إلكتروني للبيع على الإنترنت، إدارة داخلية كاملة، وصندوق متصل: ما تستخدمه التعاونيات فعلياً على تعاونيتي، مع صلاحيات وباقات تناسب كل مرحلة.'}
            </p>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                  <polyline points="9 22 9 12 15 12 15 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
              </div>
              <h3>{language === 'fr' ? 'Site web de votre coopérative' : 'موقع تعاونيتكم الإلكتروني'}</h3>
              <p>
                {language === 'fr' ? 'Un site à votre adresse (ex. votredomaine/votre-coop) : fiches produits, panier et commandes avec suivi.' : 'موقع على عنوانكم: منتجات، سلة وطلبات مع متابعة.'}
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="2" fill="none" />
                  <path d="M6 8h12M6 12h8M6 16h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <h3>{terms.posSystemCap}</h3>
              <p>
                {language === 'fr'
                  ? `Encaissement au comptoir, panier, tickets et synchronisation du stock avec votre site web et le ${terms.erp} (forfait Écosystème complet).`
                  : `صندوق في المحل، سلة، إيصالات ومزامنة المخزون مع الموقع الإلكتروني و${terms.erp} (باقة المنظومة المتكاملة).`}
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <path d="M3 3v18h18V3H3zm16 16H5V5h14v14z" fill="currentColor" />
                  <path d="M7 7h10v2H7V7zm0 4h10v2H7v-2zm0 4h7v2H7v-2z" fill="currentColor" />
                </svg>
              </div>
              <h3>{language === 'fr' ? 'Suivi des Produits et Inventaire' : 'تتبع المنتجات والمخزون'}</h3>
              <p>
                {language === 'fr' ? 'Sachez toujours ce qui est en stock, recevez des alertes quand les fournitures sont faibles, et gérez vos produits et fournisseurs avec facilité.' : 'اعرف دائمًا ما هو متوفر في المخزون، واحصل على تنبيهات عندما تكون الإمدادات منخفضة، وأدر منتجاتك ومورديك بسهولة.'}
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <path d="M2 12C2 6.48 6.48 2 12 2s10 4.48 10 10-4.48 10-10 10S2 17.52 2 12z" fill="currentColor" />
                  <path d="M12 6v6l4 2" stroke="white" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <h3>{language === 'fr' ? 'Simplification des Ventes et Achats' : 'تبسيط المبيعات والمشتريات'}</h3>
              <p>{language === 'fr' ? 'Enregistrez chaque vente et achat, suivez les paiements, et visualisez la santé financière de votre coopérative en temps réel.' : 'سجل كل عملية بيع وشراء، وتتبع المدفوعات، وتصور الصحة المالية لتعاونيتك في الوقت الفعلي.'}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <path d="M3 3v18l4-4h14V3H3z" fill="currentColor" />
                  <path d="M7 8h10M7 12h6" stroke="white" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </div>
              <h3>{language === 'fr' ? 'Gestion des Membres et Rôles' : 'إدارة الأعضاء والأدوار'}</h3>
              <p>{language === 'fr' ? 'Ajoutez et organisez les membres, attribuez des rôles (comme admin ou employé), et gardez tout le monde informé et impliqué.' : 'أضف ونظم الأعضاء، وعين الأدوار (مثل المدير أو الموظف)، وأبقِ الجميع على اطلاع ومشارك.'}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" fill="currentColor" />
                  <circle cx="8.5" cy="7" r="4" fill="currentColor" />
                  <path
                    d="M20 8v6M23 11l-3 3-3-3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3>{language === 'fr' ? 'Rapports et Notifications Automatiques' : 'التقارير والتنبيهات التلقائية'}</h3>
              <p>{language === 'fr' ? 'Obtenez des rapports automatiques sur la performance de votre coopérative, et recevez des notifications pour les événements importants.' : 'احصل على تقارير تلقائية حول أداء تعاونيتك، واستلم إشعارات للأحداث المهمة.'}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" fill="currentColor" />
                  <path
                    d="M8 21l4-7 4 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3>{language === 'fr' ? 'Support Multilingue' : 'الدعم متعدد اللغات'}</h3>
              <p>{language === 'fr' ? 'Utilisez la plateforme en arabe, français ou anglais—ce qui est le plus facile pour votre équipe.' : 'استخدم المنصة بالعربية أو الفرنسية أو الإنجليزية—ما هو أسهل لفريقك.'}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" fill="currentColor" />
                  <circle cx="12" cy="16" r="1" fill="white" />
                  <path
                    d="M7 11V7a5 5 0 0 1 10 0v4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3>{language === 'fr' ? 'Sécurisé et Accessible Partout' : 'آمن ومتاح في كل مكان'}</h3>
              <p>{language === 'fr' ? 'Vos données sont sûres, sauvegardées, et accessibles depuis n\'importe quel appareil—ordinateur, tablette ou téléphone.' : 'بياناتك آمنة ومحفوظة ومتاحة من أي جهاز—كمبيوتر أو جهاز لوحي أو هاتف.'}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="benefits">
        <div className="container">
          <div className="benefits-content">
            <div className="benefits-text">
              <div className="section-badge">{language === 'fr' ? 'Pourquoi Taaouniaty' : 'لماذا تعاونيتي'}</div>
              <h2 className="benefits-main-title">{language === 'fr' ? 'Une plateforme qui s’adapte à la taille de votre coopérative' : 'منصة تتكيف مع حجم تعاونيتكم'}</h2>
              <p className="benefits-intro">
                {language === 'fr'
                  ? `Commencez léger avec un site web, structurez avec le ${terms.erp}, déployez le ${terms.posSystem} quand vous êtes prêts — la même équipe, la même vision produit.`
                  : `ابدؤوا بموقع إلكتروني، ثم ${terms.erp}، ثم ${terms.posSystem} عند الجاهزية — نفس الفريق ونفس المنتج.`}
              </p>
              <div className="benefits-list">
                <div className="benefit-item">
                  <div className="benefit-icon">⚡</div>
                  <div className="benefit-content">
                    <h4 className="benefit-item-title">{language === 'fr' ? 'Économise du temps et réduit les erreurs' : 'وفر الوقت وقلل الأخطاء'}</h4>
                    <p className="benefit-item-text">
                      {language === 'fr' ? 'Automatisez les tâches répétitives et évitez les erreurs. Plus de temps pour vous concentrer sur la croissance.' : 'أتمتة المهام المتكررة وتجنب الأخطاء. المزيد من الوقت للتركيز على النمو.'}
                    </p>
                  </div>
                </div>

                <div className="benefit-item">
                  <div className="benefit-icon">🎓</div>
                  <div className="benefit-content">
                    <h4 className="benefit-item-title">{language === 'fr' ? 'Support dédié et formation' : 'دعم مخصص وتدريب'}</h4>
                    <p className="benefit-item-text">{language === 'fr' ? 'Une équipe à votre écoute pour vous accompagner à chaque étape, avec formation et support technique inclus.' : 'فريق في خدمتك لمساعدتك في كل خطوة، مع التدريب والدعم التقني المدرج.'}</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">🇲🇦</div>
                  <div className="benefit-content">
                    <h4 className="benefit-item-title">{language === 'fr' ? 'Conçu pour les coopératives marocaines' : 'مصمم للتعاونيات المغربية'}</h4>
                    <p className="benefit-item-text">{language === 'fr' ? 'Développé spécifiquement pour répondre aux besoins uniques des coopératives au Maroc.' : 'مطور خصيصًا للاستجابة للاحتياجات الفريدة للتعاونيات في المغرب.'}</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">💡</div>
                  <div className="benefit-content">
                    <h4 className="benefit-item-title">{language === 'fr' ? 'Transparence et efficacité' : 'الشفافية والكفاءة'}</h4>
                    <p className="benefit-item-text">{language === 'fr' ? 'Rendez la gestion simple, transparente et efficace pour vous concentrer sur ce qui compte : servir votre communauté.' : 'اجعل الإدارة بسيطة وشفافة وفعالة للتركيز على ما يهم: خدمة مجتمعك.'}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="benefits-visual">
              <div className="benefits-card">
                <div className="benefits-card-header">
                  <h3>{language === 'fr' ? 'Résultats Moyens' : 'النتائج المتوسطة'}</h3>
                  <span className="benefits-period">{language === 'fr' ? 'Après 6 mois' : 'بعد 6 أشهر'}</span>
                </div>
                <div className="benefits-stats">
                  <div className="benefit-stat">
                    <div className="benefit-stat-number">-70%</div>
                    <div className="benefit-stat-label">{language === 'fr' ? 'Temps de gestion' : 'وقت الإدارة'}</div>
                    <div className="benefit-stat-bar">
                      <div className="benefit-stat-fill" style={{ width: "70%" }}></div>
                    </div>
                  </div>
                  <div className="benefit-stat">
                    <div className="benefit-stat-number">-85%</div>
                    <div className="benefit-stat-label">{language === 'fr' ? 'Erreurs comptables' : 'الأخطاء المحاسبية'}</div>
                    <div className="benefit-stat-bar">
                      <div className="benefit-stat-fill" style={{ width: "85%" }}></div>
                    </div>
                  </div>
                  <div className="benefit-stat">
                    <div className="benefit-stat-number">+45%</div>
                    <div className="benefit-stat-label">{language === 'fr' ? 'Productivité' : 'الإنتاجية'}</div>
                    <div className="benefit-stat-bar">
                      <div className="benefit-stat-fill" style={{ width: "45%" }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Pricing Plans Component */}
      <section id="pricing" className="pricing-section-wrapper">
        <Plans language={language} />
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <div className="cta-text">
              <h2>{language === 'fr' ? 'L’infrastructure digitale de votre coopérative' : 'البنية الرقمية لتعاونيتكم'}</h2>
              <p>{language === 'fr'
                ? `Du site web au ${terms.erp} et au ${terms.pos}, Taaouniaty vous accompagne à chaque étape — gestion claire, équipe alignée, communauté mieux servie.`
                : `من الموقع الإلكتروني إلى ${terms.erp} و${terms.posSystem}، تعاونيتي معكم في كل مرحلة — إدارة أوضح وفريق متناسق وخدمة أفضل للمجتمع.`}</p>
              <div className="cta-features">
                <span>✓ {language === 'fr' ? 'Essai gratuit 14 jours' : 'تجربة مجانية 14 يوم'}</span>
                <span>✓ {language === 'fr' ? 'Aucune carte de crédit requise' : 'لا توجد بطاقة ائتمان مطلوبة'}</span>
                <span>✓ {language === 'fr' ? 'Configuration en 24h' : 'التكوين في 24 ساعة'}</span>
                <span>✓ {language === 'fr' ? 'Documentation & support en ligne' : 'وثائق ودعم عبر الإنترنت'}</span>
              </div>
            </div>
            <div className="cta-buttons">
              <button className="btn-primary large" onClick={() => window.location.href = '/register'}>
                {language === 'fr' ? 'Commencer Gratuitement' : 'ابدأ مجاناً'}
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12h14M12 5l7 7-7 7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-brand">
                <h3>Taaouniaty</h3>
                <span className="brand-tagline">Pro</span>
              </div>
              <p>
                {language === 'fr'
                  ? `Infrastructure digitale pour coopératives : site web, ${terms.erp} et ${terms.posSystem} sur une seule plateforme. Simplifiez la gestion et grandissez à votre rythme.`
                  : `بنية رقمية للتعاونيات: موقع إلكتروني و${terms.erp} و${terms.posSystem} في منصة واحدة. بسّطوا الإدارة ونموّوا على وتيرتكم.`}
              </p>
              <div className="social-links">
                <a href="#" aria-label="Facebook" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#f97316', color: 'white', textDecoration: 'none', marginRight: '10px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="https://www.linkedin.com/company/taaouniaty-pro/" aria-label="LinkedIn" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#f97316', color: 'white', textDecoration: 'none', marginRight: '10px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a href="#" aria-label="Twitter" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#f97316', color: 'white', textDecoration: 'none', marginRight: '10px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </a>
                <a href="https://www.instagram.com/taaouniaty/" aria-label="Instagram" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#f97316', color: 'white', textDecoration: 'none' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
              </div>
            </div>
            <div className="footer-section">
              <h4>{language === 'fr' ? 'Produit' : 'المنتج'}</h4>
              <ul>
                <li>
                  <a href="#features">{language === 'fr' ? 'Fonctionnalités' : 'الميزات'}</a>
                </li>
                <li>
                  <a href="#pricing">{language === 'fr' ? 'Tarifs' : 'الأسعار'}</a>
                </li>
                <li>
                  <a href="/marketplace" onMouseEnter={() => prefetchShopCatalog().catch(() => {})} onFocus={() => prefetchShopCatalog().catch(() => {})}>{language === 'fr' ? 'Marketplace' : 'المتجر'}</a>
                </li>
                <li>
                  <a href="/article/blog">{language === 'fr' ? 'Articles' : 'المقالات'}</a>
                </li>
                <li>
                  <a href="/article/cooperatives">{language === 'fr' ? 'Rencontrez nos coopératives' : 'تعاونياتنا'}</a>
                </li>
                <li>
                  <a href="#">{language === 'fr' ? 'Sécurité' : 'الأمان'}</a>
                </li>
                <li>
                  <a href="#">{language === 'fr' ? 'Intégrations' : 'التكاملات'}</a>
                </li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>{language === 'fr' ? 'Support' : 'الدعم'}</h4>
              <ul>
                <li>
                  <a href="#">{language === 'fr' ? 'Centre d\'aide' : 'مركز المساعدة'}</a>
                </li>
                <li>
                  <a href="#">{language === 'fr' ? 'Documentation' : 'الوثائق'}</a>
                </li>
                <li>
                  <a href="#">{language === 'fr' ? 'Formation' : 'التدريب'}</a>
                </li>
                <li>
                  <a href="#">{language === 'fr' ? 'Status' : 'الحالة'}</a>
                </li>
              </ul>
            </div>
            <div className="footer-section">
              <h4>{language === 'fr' ? 'Contact' : 'اتصل بنا'}</h4>
              <div className="contact-info">
                <p>📧 taaouniaty@gmail.com</p>
                <p>📞 +212 676263023</p>
                <p>📍 {language === 'fr' ? 'Marrakech, Maroc' : 'مراكش، المغرب'}</p>
              </div>
              <PreferredSourceButton language={language} compact />
            </div>
          </div>
          <div className="footer-bottom">
            <div className="footer-bottom-left">
              <p>&copy; 2026 Taaouniaty Pro. {language === 'fr' ? 'Tous droits réservés.' : 'جميع الحقوق محفوظة.'}</p>
            </div>
            <div className="footer-bottom-right">
              <a href="#">{language === 'fr' ? 'Confidentialité' : 'الخصوصية'}</a>
              <a href="#">{language === 'fr' ? 'Conditions' : 'الشروط'}</a>
              <a href="#">{language === 'fr' ? 'Cookies' : 'ملفات تعريف الارتباط'}</a>
            </div>
          </div>
        </div>
      </footer>
      
      {showWelcomePopup && (
        <WelcomePopup
          language={language}
          isNewUser={false}
          onClose={() => {
            setShowWelcomePopup(false)
            localStorage.setItem('hasSeenWelcomePopup', 'true')
          }}
        />
      )}
    </div>
  )
}
